/**
 * MarkdownMessage — renders assistant chat text as markdown.
 *
 * The Explain agent's system prompt now emits markdown (bold, bullet /
 * numbered lists, and occasionally GFM tables). This surface previously
 * rendered assistant text as a plain <Typography sx={{ whiteSpace:
 * 'pre-wrap' }}>, so markdown syntax showed up as literal characters
 * (e.g. "**bold**", "| a | b |"). This component replaces that Typography
 * for assistant_text turns only.
 *
 * v2-local by design: this intentionally duplicates rather than imports
 * src/components/CloudCastAI/sections/RightSection/components/MarkdownMessage.tsx,
 * which belongs to the v1 surface and is being phased out. Do not
 * cross-import between the two; some duplication here is deliberate.
 *
 * Newline handling (why this isn't a naive react-markdown swap):
 * CommonMark treats a single "\n" inside a paragraph as a "soft break" —
 * renderers may collapse it to a space. remark/react-markdown do NOT
 * convert soft breaks into <br/>; the literal "\n" character is preserved
 * in the resulting text node. A browser's default `white-space: normal`
 * then collapses that character to a space, silently eating single-newline
 * formatting the backend relies on. The `p` override below sets
 * `whiteSpace: 'pre-wrap'` — the same value the previous plain <Typography>
 * used — so a single newline still renders as a visual line break.
 * Paragraph-to-paragraph spacing (a blank line in the source) is handled
 * separately: remark turns that into two sibling <p> elements, which get a
 * small bottom margin here (removed on the last child) so consecutive
 * paragraphs / lists don't collide now that pre-wrap alone can't create the
 * gap the way it did for a single unparsed text blob.
 *
 * Tables:
 * react-markdown v10 does not parse GFM tables ("| a | b |\n| --- | --- |")
 * on its own — that needs the `remark-gfm` plugin, which is a dependency of
 * this repo for exactly this reason and is passed via `remarkPlugins` below.
 * The table / thead / tbody / tr / th / td overrides then give the result the
 * same muted MUI styling as the rest of the chat surface. The chat bubble is
 * capped at 80% width, so a table wider than that scrolls horizontally within
 * the bubble instead of overflowing it — hence `display: block` plus
 * `overflowX: auto` on the table element.
 */
import React, { type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownMessageProps {
  content: string;
}

interface ComponentProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node?: any;
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const components: Components = {
  p: ({ node, ...props }: ComponentProps) => (
    <Typography
      variant="body2"
      component="p"
      sx={{ m: 0, mb: 1, whiteSpace: 'pre-wrap', '&:last-child': { mb: 0 } }}
      {...props}
    />
  ),
  strong: ({ node, ...props }: ComponentProps) => (
    <strong style={{ fontWeight: 600 }} {...props} />
  ),
  em: ({ node, ...props }: ComponentProps) => (
    <em style={{ fontStyle: 'italic' }} {...props} />
  ),
  ul: ({ node, ...props }: ComponentProps) => (
    <Box
      component="ul"
      sx={{ m: 0, mb: 1, pl: 2.5, '&:last-child': { mb: 0 } }}
      {...props}
    />
  ),
  ol: ({ node, ...props }: ComponentProps) => (
    <Box
      component="ol"
      sx={{ m: 0, mb: 1, pl: 2.5, '&:last-child': { mb: 0 } }}
      {...props}
    />
  ),
  li: ({ node, ...props }: ComponentProps) => (
    <Box
      component="li"
      sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 0.5 }}
      {...props}
    />
  ),
  code: ({ node, ...props }: ComponentProps) => (
    <Box
      component="code"
      sx={{
        bgcolor: 'grey.100',
        px: 0.5,
        py: 0.125,
        borderRadius: 0.5,
        fontFamily: 'monospace',
        fontSize: '0.8rem',
      }}
      {...props}
    />
  ),
  pre: ({ node, ...props }: ComponentProps) => (
    <Box
      component="pre"
      sx={{
        bgcolor: 'grey.100',
        p: 1.5,
        borderRadius: 1,
        overflowX: 'auto',
        m: 0,
        mb: 1,
        '&:last-child': { mb: 0 },
        '& code': { bgcolor: 'transparent', p: 0 },
      }}
      {...props}
    />
  ),
  blockquote: ({ node, ...props }: ComponentProps) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: '3px solid',
        borderColor: 'grey.300',
        pl: 1.5,
        ml: 0,
        mr: 0,
        mb: 1,
        color: 'text.secondary',
        '&:last-child': { mb: 0 },
      }}
      {...props}
    />
  ),
  // GFM tables (require remarkGfm, passed below). `display: block` +
  // `overflowX: auto` keeps a wide table scrollable inside the chat bubble
  // rather than stretching it past its 80% cap.
  table: ({ node, ...props }: ComponentProps) => (
    <Box
      component="table"
      sx={{
        display: 'block',
        width: 'max-content',
        maxWidth: '100%',
        overflowX: 'auto',
        borderCollapse: 'collapse',
        fontSize: '0.8rem',
        mb: 1,
        '&:last-child': { mb: 0 },
      }}
      {...props}
    />
  ),
  thead: ({ node, ...props }: ComponentProps) => (
    <Box component="thead" sx={{ bgcolor: 'grey.100' }} {...props} />
  ),
  tbody: ({ node, ...props }: ComponentProps) => (
    <Box component="tbody" {...props} />
  ),
  tr: ({ node, ...props }: ComponentProps) => (
    <Box
      component="tr"
      sx={{ '&:last-child td': { borderBottom: 'none' } }}
      {...props}
    />
  ),
  // remark-gfm emits an inline `style={{ textAlign }}` for columns using the
  // `|:--|--:|` alignment syntax; spreading props last lets that win.
  th: ({ node, ...props }: ComponentProps) => (
    <Box
      component="th"
      sx={{
        px: 1,
        py: 0.5,
        textAlign: 'left',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        borderBottom: '1px solid',
        borderColor: 'grey.300',
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }: ComponentProps) => (
    <Box
      component="td"
      sx={{
        px: 1,
        py: 0.5,
        verticalAlign: 'top',
        borderBottom: '1px solid',
        borderColor: 'grey.200',
      }}
      {...props}
    />
  ),
};

// Hoisted alongside `components` so neither identity changes between renders.
const remarkPlugins = [remarkGfm];

export const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
}) => (
  <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
    {content}
  </ReactMarkdown>
);
